/**
 * @file ErrorBoundary.jsx
 * @description Error boundary component with telemetry integration
 * @workItem error_boundary_telemetry
 */

import React, { useRef, useEffect } from 'react';
import { tracer } from '../utils/telemetry/simpleTracer';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      retryCount: 0
    };
    this.retryButtonRef = React.createRef();
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorCount = this.state.errorCount + 1;
    
    // Log error to tracing service
    tracer.recordError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'default',
      errorCount,
      props: JSON.stringify(this.props),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorCount
    });

    // Call custom error handler if provided, otherwise log to console
    const errorHandler = this.props.onError || ((err, info) => {
      console.error('ErrorBoundary caught an error:', err, info);
    });
    errorHandler(error, errorInfo);

    // Log to console in development
    if (import.meta.env.DEV || this.props.showError) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Auto-focus retry button when error occurs and retryable
    if (!prevState.hasError && this.state.hasError && this.props.retryable !== false) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (this.retryButtonRef.current) {
          this.retryButtonRef.current.focus();
        }
      }, 0);
    }
  }

  handleReset = () => {
    const { maxRetries = Infinity } = this.props;
    
    if (this.state.retryCount >= maxRetries) {
      return; // Don't allow reset if max retries reached
    }

    // Increment retry count
    const retryCount = this.state.retryCount + 1;

    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount
    });

    // Track recovery attempt
    tracer.startSpan('error.recovery', {
      'recovery.boundary': this.props.name || 'default',
      'recovery.error_count': this.state.errorCount,
      'recovery.retry_count': retryCount
    }).end();

    // Call custom reset handler if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const {
        fallback,
        retryable = true,
        maxRetries = Infinity,
        showError = false,
        errorTitle = 'Something went wrong',
        errorMessage = 'We apologize for the inconvenience. Please try again.',
        showContactSupport = false,
        showReloadOption = false
      } = this.props;

      const canRetry = retryable && this.state.retryCount < maxRetries;
      const isDevelopment = import.meta.env.DEV;
      const shouldShowError = showError || isDevelopment;

      // Custom fallback UI if provided
      if (fallback) {
        // Support both React elements and render functions
        if (typeof fallback === 'function') {
          return fallback(
            this.state.error,
            this.state.errorInfo,
            this.handleReset
          );
        }
        return fallback;
      }

      // Default fallback UI
      return (
        <div 
          className="error-boundary-fallback" 
          role="alert"
          aria-live="polite"
          style={{
            padding: '2rem',
            textAlign: 'center',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <h2 style={{ color: '#dc2626', marginBottom: '1rem' }}>
            {errorTitle}
          </h2>
          <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
            {errorMessage}
          </p>
          
          {shouldShowError && this.state.error && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '0.5rem',
              maxWidth: '600px',
              textAlign: 'left'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                {this.state.error.toString()}
              </p>
              {isDevelopment && (
                <details style={{ marginTop: '0.5rem' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                    Component Stack
                  </summary>
                  <pre style={{
                    marginTop: '0.5rem',
                    fontSize: '0.875rem',
                    overflow: 'auto'
                  }}>
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}

          {this.state.retryCount >= maxRetries && maxRetries !== Infinity && (
            <p style={{ marginTop: '1rem', color: '#dc2626', fontWeight: 'bold' }}>
              Maximum retry attempts reached
            </p>
          )}

          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
            {canRetry && (
              <button
                ref={this.retryButtonRef}
                onClick={this.handleReset}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                Try Again
              </button>
            )}
            
            {showReloadOption && (
              <button
                onClick={this.handleReload}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
              >
                Reload Page
              </button>
            )}
            
            {showContactSupport && (
              <a
                href="/support"
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
              >
                Contact Support
              </a>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap any component with error boundary
 * @param {React.Component} Component - Component to wrap
 * @param {Object} errorBoundaryProps - Props for error boundary
 */
export function withErrorBoundary(Component, errorBoundaryProps = {}) {
  return function WithErrorBoundaryComponent(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

/**
 * Hook to trigger error boundary (for testing)
 */
export function useErrorHandler() {
  return (error) => {
    throw error;
  };
}

export default ErrorBoundary;